import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { getUserAlias } from 'models/ProfileInfo/selectors'
import { COLOURS, STRING } from 'resources'
import { checkCircleGreen, exclamationOrange, warningCircleRed, editPencil } from 'resources/images'
import { getReklaimProtectUserProfile, getReklaimProtectEmailAddresses } from 'services/reklaimProtect'

import { formatProfileInfo } from './utils'
import {
  Container,
  TitleContainer,
  Spinner,
  IconContainer,
  Icon,
  Title,
  GreyTitle,
  EditIcon,
  Description,
} from './styles'

const ProfileInfo = ({
  profileInfo,
  setProfileInfo,
  isProfileInfoLoading,
  setIsProfileInfoLoading,
  setEditModalOpen,
}) => {
  const userAlias = useSelector(getUserAlias)
  const formattedProfileInfo = useMemo(
    () => (!isProfileInfoLoading ? formatProfileInfo(profileInfo) : null),
    [isProfileInfoLoading, profileInfo],
  )

  const hasCompletedProfile = useMemo(() => {
    if (!formattedProfileInfo || formattedProfileInfo?.error) return false
    // Missing info if profileInfo.item.value contains emDash
    return Object.values(formattedProfileInfo).every(({ value }) => !value.includes(STRING.emDash))
  }, [formattedProfileInfo])

  const fetchProfileInfo = useCallback(async () => {
    setIsProfileInfoLoading(true)
    const protectUserProfile = await getReklaimProtectUserProfile(userAlias)
    const protectEmailAddresses = await getReklaimProtectEmailAddresses(userAlias)
    setIsProfileInfoLoading(false)
    setProfileInfo({ userProfile: protectUserProfile, emailAddresses: protectEmailAddresses })
  }, [userAlias, setProfileInfo, setIsProfileInfoLoading])

  useEffect(() => {
    fetchProfileInfo()
  }, [fetchProfileInfo])

  let content
  if (formattedProfileInfo === null) {
    content = (
      <TitleContainer>
        <Spinner />
        <Title>{STRING.protect.profileInfo.title}</Title>
      </TitleContainer>
    )
  } else if (formattedProfileInfo?.error) {
    content = (
      <>
        <TitleContainer>
          <IconContainer color={COLOURS.secondaryRed2}>
            <Icon src={warningCircleRed} />
          </IconContainer>
          <Title>{STRING.protect.profileInfo.title}</Title>
        </TitleContainer>
        <Description>{STRING.protect.profileInfo.descriptionError}</Description>
      </>
    )
  } else {
    content = (
      <>
        <TitleContainer>
          <IconContainer color={hasCompletedProfile ? COLOURS.systemGreen2 : COLOURS.systemOrange2}>
            <Icon src={hasCompletedProfile ? checkCircleGreen : exclamationOrange} />
          </IconContainer>
          <Title>
            {STRING.protect.profileInfo.title}
            <GreyTitle>
              {hasCompletedProfile
                ? STRING.protect.profileInfo.titleComplete
                : STRING.protect.profileInfo.titleInfoMissing}
            </GreyTitle>
          </Title>
          <EditIcon src={editPencil} onClick={() => setEditModalOpen(true)} />
        </TitleContainer>
        <Description>{STRING.protect.profileInfo.description}</Description>
        {formattedProfileInfo &&
          !hasCompletedProfile &&
          Object.entries(formattedProfileInfo).map(([key, { label, value }]) => (
            <Description key={key}>
              {label}
              {value}
            </Description>
          ))}
      </>
    )
  }

  return <Container>{content}</Container>
}

ProfileInfo.propTypes = {
  profileInfo: PropTypes.oneOfType([
    PropTypes.shape({
      error: PropTypes.bool,
    }),
    PropTypes.shape({
      userProfile: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        address: PropTypes.string,
        address2: PropTypes.string,
        city: PropTypes.string,
        state: PropTypes.string,
        zip: PropTypes.string,
        birthDate: PropTypes.string,
      }),
      emailAddresses: PropTypes.arrayOf(
        PropTypes.shape({
          emailAddress: PropTypes.string.isRequired,
          isPrimary: PropTypes.bool.isRequired,
        }),
      ),
    }),
  ]).isRequired,
  setProfileInfo: PropTypes.func.isRequired,
  isProfileInfoLoading: PropTypes.bool.isRequired,
  setIsProfileInfoLoading: PropTypes.func.isRequired,
  setEditModalOpen: PropTypes.func.isRequired,
}

export default ProfileInfo
